import { useEffect, useRef, useState } from 'react';

import { useExportReportMutation } from '@backrs_ui/grow-ui/src/redux/coachingDashboard';
import { downloadBlob, downloadFromUrl } from '@backrs_ui/grow-ui/src/utils/downloadFile';

import { EXPORT_REPORTS } from './reports.config';
import { type ReportExportStatus, ReportsModalView } from './ReportsModalView';

type ReportsModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
}>;

export const ReportsModal = ({ open, onClose }: ReportsModalProps) => {
  const [triggerExport] = useExportReportMutation();

  const [selectedKey, setSelectedKey] = useState('');
  const [status, setStatus] = useState<ReportExportStatus>('idle');

  // The modal stays mounted while closed, so an in-flight export can outlive a
  // reselect or close/reopen and resolve against a stale selection.
  const activeDownloadId = useRef(0);

  useEffect(() => {
    if (open) {
      activeDownloadId.current += 1;
      setSelectedKey('');
      setStatus('idle');
    }
  }, [open]);

  const handleSelect = (key: string) => {
    activeDownloadId.current += 1;
    setSelectedKey(key);
    setStatus('idle');
  };

  const handleDownload = async () => {
    const report = EXPORT_REPORTS.find((candidate) => candidate.key === selectedKey);

    if (!report) return;

    const requestId = (activeDownloadId.current += 1);

    setStatus('loading');

    try {
      const download = await triggerExport({
        reportKey: report.key,
      }).unwrap();

      if (requestId !== activeDownloadId.current) return;

      const isEmpty = download.kind === 'blob' ? download.blob.size === 0 : download.rowCount === 0;

      if (isEmpty) {
        setStatus('zero');

        return;
      }

      if (download.kind === 'blob') {
        downloadBlob(download.blob, download.filename);
      } else {
        downloadFromUrl(download.url, download.filename);
      }

      setStatus('idle');
    } catch (error) {
      if (requestId !== activeDownloadId.current) return;

      console.error(`Failed to export ${report.key}:`, error);
      setStatus('error');
    }
  };

  return (
    <ReportsModalView
      open={open}
      onClose={onClose}
      reports={EXPORT_REPORTS}
      selectedKey={selectedKey}
      onSelect={handleSelect}
      status={status}
      onDownload={handleDownload}
    />
  );
};
